import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TJobRoleAvailability,
  TProjectTemplate,
  TWorkspaceCapacityGroupBy,
  TWorkspaceCapacityRowGroup,
  TCapacityProjectLoad,
  TWorkspaceCapacitySummary,
  TWorkspaceCapacityTrajectoryAggregateResponse,
  TWorkspaceCapacityTrajectoryResponse,
  TWorkspaceCapacityTriageResponse,
  TWorkspaceCapacityTriageSegment,
  TWorkspaceCapacityTotals,
} from "@/types/workspace-capacity";
import type {
  TTaskToUsersMatchResponse,
  TTaskUserMatchRequestOptions,
  TUserTaskMatchRequestOptions,
  TUserToTasksMatchResponse,
} from "@/types/task-matching";

type TCapacityRequestOptions = {
  /** The request being planned (NG-146). Selects the request-planner arm of the
   *  server's capacity-visibility rule, which bounds the read to the caller's own
   *  team. Only ever narrowing: a caller holding a broader capacity grant is
   *  unaffected by it. */
  submissionId?: string;
  includeDetails?: boolean;
  includeMembers?: boolean;
  includeGroupMembers?: boolean;
  includeTotals?: boolean;
  /** When false, the grouped read skips per-member remaining/availability-state
   * (callers that derive remaining client-side and don't render states). */
  includeMemberMetrics?: boolean;
  memberPageSize?: number;
  memberCursor?: string | null;
  memberIds?: string[];
  groupPageSize?: number;
  groupCursor?: string | null;
  rowGroup?: TWorkspaceCapacityRowGroup;
  statusFilter?: string;
  targetPercent?: number;
  minPercent?: number;
  requiredSkillIds?: string[];
  teamIds?: string[];
  projectIds?: string[];
  programIds?: string[];
  projectStateIds?: string[];
  jobRoleIds?: string[];
  includeArchivedProjects?: boolean;
  /** Free-text people filter (display_name substring), applied server-side so
   * it narrows the member set before pagination. */
  search?: string;
};

type TCapacityFilterOptions = {
  statusFilter?: string;
  requiredSkillIds?: string[];
  teamIds?: string[];
  projectIds?: string[];
  programIds?: string[];
  /** Project STATUSES to keep. Narrows the allocations, not just the people. */
  projectStateIds?: string[];
  jobRoleIds?: string[];
  /** Whether allocations from archived projects count. Left OUT, the server keeps
   *  its pre-NG-1458 answer (they do), which an older bundle still relies on —
   *  so this is only ever sent by a caller that means it. */
  includeArchivedProjects?: boolean;
  search?: string;
};

type TCapacityDetailsRequestOptions = TCapacityFilterOptions & {
  memberIds?: string[];
  /** Offset/limit into the member's distinct task list (server task pagination). */
  taskOffset?: number;
  taskLimit?: number;
};

type TCapacityTotalsRequestOptions = {
  memberIds?: string[];
  teamIds?: string[];
};

type TCapacityTriageRequestOptions = {
  segment: TWorkspaceCapacityTriageSegment;
  memberPageSize?: number;
  memberCursor?: string | null;
  memberIds?: string[];
  underPercent?: number;
  overPercent?: number;
};

type TCapacityTrajectoryRequestOptions = {
  memberPageSize?: number;
  memberCursor?: string | null;
};

function appendMemberIds(params: URLSearchParams, memberIds: string[] | undefined): void {
  if (memberIds?.length) params.set("member_ids", memberIds.join(","));
}

function appendRequiredSkillIds(params: URLSearchParams, ids: string[] | undefined): void {
  if (!ids || ids.length === 0) return;
  for (const id of ids) params.append("required_skill_id", id);
}

function appendCapacityPageOptions(
  params: URLSearchParams,
  options: TCapacityRequestOptions,
): void {
  if (options.submissionId) params.set("submission_id", options.submissionId);
  if (options.includeDetails === true) params.set("include_details", "true");
  if (options.includeMembers === false) params.set("include_members", "false");
  if (options.includeGroupMembers === false) params.set("include_group_members", "false");
  if (options.includeTotals === false) params.set("include_totals", "false");
  if (options.includeMemberMetrics === false) params.set("include_member_metrics", "false");
  if (typeof options.memberPageSize === "number")
    params.set("member_page_size", String(options.memberPageSize));
  if (options.memberCursor) params.set("member_cursor", options.memberCursor);
  if (typeof options.groupPageSize === "number")
    params.set("group_page_size", String(options.groupPageSize));
  if (options.groupCursor) params.set("group_cursor", options.groupCursor);
  if (options.rowGroup && options.rowGroup !== "member") params.set("row_group", options.rowGroup);
  appendMemberIds(params, options.memberIds);
  if (typeof options.targetPercent === "number")
    params.set("target_percent", String(options.targetPercent));
  if (typeof options.minPercent === "number") params.set("min_percent", String(options.minPercent));
  appendCapacityFilters(params, options);
}

/** The toolbar filters, shared by the summary read and the per-member details
 * read so an expanded row cannot describe a different population than the row
 * it opened from. */
function appendCapacityFilters(params: URLSearchParams, options: TCapacityFilterOptions): void {
  if (options.statusFilter) params.set("status_filter", options.statusFilter);
  appendRequiredSkillIds(params, options.requiredSkillIds);
  if (options.teamIds?.length) params.set("team_ids", options.teamIds.join(","));
  if (options.projectIds?.length) params.set("project_ids", options.projectIds.join(","));
  if (options.programIds?.length) params.set("program_ids", options.programIds.join(","));
  if (options.projectStateIds?.length)
    params.set("project_state_ids", options.projectStateIds.join(","));
  if (options.jobRoleIds?.length) params.set("job_role_ids", options.jobRoleIds.join(","));
  if (typeof options.includeArchivedProjects === "boolean")
    params.set("include_archived_projects", options.includeArchivedProjects ? "true" : "false");
  if (options.search) params.set("search", options.search);
}

function appendTaskMatchOptions(
  params: URLSearchParams,
  options: TUserTaskMatchRequestOptions | TTaskUserMatchRequestOptions,
): void {
  if (options.startDate) params.set("start_date", options.startDate);
  if (options.endDate) params.set("end_date", options.endDate);
  if (options.limit) params.set("limit", String(options.limit));
}

export class WorkspaceCapacityService extends ApiService {
  async getWorkspaceCapacity(
    slug: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityRequestOptions = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacitySummary> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    appendCapacityPageOptions(params, options);
    return this.get(`/api/workspaces/${slug}/capacity-allocations/?${params.toString()}`, config);
  }

  async getWorkspaceCapacityTotals(
    slug: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityTotalsRequestOptions = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacityTotals> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    appendMemberIds(params, options.memberIds);
    if (options.teamIds?.length) params.set("team_ids", options.teamIds.join(","));
    return this.get(
      `/api/workspaces/${slug}/capacity-allocations/totals/?${params.toString()}`,
      config,
    );
  }

  async getWorkspaceCapacityDetails(
    slug: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityDetailsRequestOptions = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacitySummary> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    appendMemberIds(params, options.memberIds);
    appendCapacityFilters(params, options);
    if (typeof options.taskLimit === "number") params.set("task_limit", String(options.taskLimit));
    if (typeof options.taskOffset === "number")
      params.set("task_offset", String(options.taskOffset));
    return this.get(
      `/api/workspaces/${slug}/capacity-allocations/details/?${params.toString()}`,
      config,
    );
  }

  /** Each project's allocated minutes for ``memberIds`` over the window (NG-2460):
   *  what the details read's allocations add up to per project, without the
   *  details. The risk strip ranks the projects it asks about by it. Omit
   *  ``memberIds`` to sum everyone the caller may see. */
  async getWorkspaceCapacityProjectLoad(
    slug: string,
    startDate: string,
    endDate: string,
    memberIds: string[] | undefined,
    config?: ApiRequestConfig,
  ): Promise<TCapacityProjectLoad> {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    appendMemberIds(params, memberIds);
    return this.get(
      `/api/workspaces/${slug}/capacity-allocations/project-load/?${params.toString()}`,
      config,
    );
  }

  async getWorkspaceCapacityTriageSegment(
    slug: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityTriageRequestOptions,
  ): Promise<TWorkspaceCapacityTriageResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
      segment: options.segment,
    });
    if (typeof options.memberPageSize === "number")
      params.set("member_page_size", String(options.memberPageSize));
    if (options.memberCursor) params.set("member_cursor", options.memberCursor);
    if (typeof options.underPercent === "number")
      params.set("under_percent", String(options.underPercent));
    if (typeof options.overPercent === "number")
      params.set("over_percent", String(options.overPercent));
    appendMemberIds(params, options.memberIds);
    return this.get(`/api/workspaces/${slug}/capacity-allocations/triage/?${params.toString()}`);
  }

  async getWorkspaceCapacityTrajectory(
    slug: string,
    startDate: string,
    endDate: string,
    options: TCapacityTrajectoryRequestOptions = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacityTrajectoryResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (typeof options.memberPageSize === "number")
      params.set("member_page_size", String(options.memberPageSize));
    if (options.memberCursor) params.set("member_cursor", options.memberCursor);
    return this.get(`/api/workspaces/${slug}/capacity-trajectory/?${params.toString()}`, config);
  }

  async getWorkspaceCapacityTrajectoryAggregate(
    slug: string,
    startDate: string,
    endDate: string,
    options: { teamIds?: string[] } = {},
  ): Promise<TWorkspaceCapacityTrajectoryAggregateResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      aggregate: "true",
    });
    if (options.teamIds?.length) params.set("team_ids", options.teamIds.join(","));
    return this.get(`/api/workspaces/${slug}/capacity-trajectory/?${params.toString()}`);
  }

  async getTaskMatchesForUser(
    slug: string,
    userId: string,
    options: TUserTaskMatchRequestOptions = {},
  ): Promise<TUserToTasksMatchResponse> {
    const params = new URLSearchParams({ user_id: userId });
    appendTaskMatchOptions(params, options);
    if (options.includeAssigned) params.set("include_assigned", "true");
    return this.get(`/api/workspaces/${slug}/task-matches/?${params.toString()}`);
  }

  async getUserMatchesForTask(
    slug: string,
    options: TTaskUserMatchRequestOptions,
    config?: ApiRequestConfig,
  ): Promise<TTaskToUsersMatchResponse> {
    const params = new URLSearchParams();
    appendTaskMatchOptions(params, options);
    if (options.workItemId) params.set("work_item_id", options.workItemId);
    if (options.taskIdentifier) params.set("task_identifier", options.taskIdentifier);
    if (options.candidateScope) params.set("candidate_scope", options.candidateScope);
    return this.get(`/api/workspaces/${slug}/task-matches/?${params.toString()}`, config);
  }

  async getProjectTemplates(slug: string): Promise<TProjectTemplate[]> {
    // Sheet: Standard template USE Default On; settings READ Default Off (NG-1915).
    return this.get(`/api/workspaces/${slug}/project-templates/pickable/`);
  }

  /**
   * Per-role capacity availability over a window (NG-710) — whether anyone
   * holding each job role (optionally scoped to delivery teams) has remaining
   * capacity between the plan's dates. Degrades to null verdicts for viewers
   * without capacity visibility rather than erroring.
   */
  async getJobRoleAvailability(
    slug: string,
    params: { roleIds: string[]; teamIds?: string[]; start: string; end: string },
  ): Promise<TJobRoleAvailability> {
    const search = new URLSearchParams({
      job_role_ids: params.roleIds.join(","),
      start_date: params.start,
      end_date: params.end,
    });
    if (params.teamIds && params.teamIds.length > 0) {
      search.set("team_ids", params.teamIds.join(","));
    }
    return this.get(`/api/workspaces/${slug}/job-role-availability/?${search.toString()}`);
  }
}
